import * as dayjs from 'dayjs';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { linkByTypeAs, toSentence } from '../../utils/cjsutils';
import { Body } from '@apmg/amat';
import { parseEmbeddedAssets } from '../../utils/utils';
import {
  cleanurlOfParams,
  createImageAttrs,
  createHtmlImageAttrs,
} from '../../utils/feedImageUtils';

const DATE_FORMAT = 'ddd, DD MMM YYYY HH:mm:ss ZZ';
const Rss = () => {};

Rss.getInitialProps = async ({ query: { slug }, req, res }) => {
  const query = () => {
    return JSON.stringify({
      query: `{collection(contentAreaSlug: "${
        process.env.CONTENT_AREA_SLUG
      }", slug: "${slug.join('/')}") {
            canonicalSlug
            title
            descriptionText
            publishDate
            results(pageSize: 30) {
              items {
                ... on Story {
                  shortTitle
                }
                title
                body
                embeddedAssets {
                  audio
                  attachments
                  images
                  oembeds
                }
                contributors {
                  profile {
                    title
                  }
                }
                descriptionText
                resourceType
                canonicalSlug
                publishDate
                updatedAt
                audio {
                  title
                  credit
                  encodings {
                    format
                    mediaType
                    httpFilePath
                    filename
                    playFilePath
                    durationMs
                  }
                }
                primaryVisuals {
                  thumbnail {
                    xid
                    shortCaption
                    fallback
                    preferredAspectRatio {
                      instances {
                        url
                        width
                        height
                        imageType
                      }
                    }
                  }
                }
                ...on Link {
                  destination
                }
              }
            }
          }
        }`,
    });
  };

  const fetchFeedData = async (query) => {
    return await fetch(process.env.PRIVATE_GRAPHQL_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: query(),
    })
      .then((response) => {
        return response.json();
      })
      .then((response) => {
        return response;
      })
      .catch((err) => {
        // eslint-disable-next-line
        console.error('Error: ', err);
      });
  };

  function getImageSrc(item) {
    if (!item?.primaryVisuals?.thumbnail?.fallback) return '';
    return cleanurlOfParams(item.primaryVisuals.thumbnail.fallback);
  }

  function greaterDate(pubdate, updatedate) {
    const pbdate = dayjs(pubdate);
    const upddte = dayjs(updatedate);
    if (pbdate.isAfter(upddte)) {
      return pubdate;
    } else {
      return updatedate;
    }
  }

  function getImage(item) {
    let result;
    const primaryImg = item.primaryVisuals.thumbnail;
    const fullFallbackInstance =
      primaryImg?.preferredAspectRatio?.instances.find(
        (img) => img.url == primaryImg.fallback
      );

    if (primaryImg) {
      result = `<img ${createHtmlImageAttrs(
        primaryImg,
        fullFallbackInstance
      )} />`;
    } else {
      result = '';
    }
    return result;
  }

  function getImageXml(item) {
    let result;
    const primaryImg = item.primaryVisuals.thumbnail;
    const fullFallbackInstance =
      primaryImg?.preferredAspectRatio?.instances.find(
        (img) => img.url == primaryImg.fallback
      );

    if (primaryImg) {
      result = `
        <media:content ${createImageAttrs(primaryImg, fullFallbackInstance)} />
        <media:description type="plain">${
          primaryImg.shortCaption
        }</media:description>
        <media:thumbnail url="${getImageSrc(item)}" />
        `;
    } else {
      result = '';
    }
    return result;
  }

  if (!res) {
    return;
  }
  let xml = '<?xml version="1.0" encoding="UTF-8"?>';
  const url = req.url.replace(/\/feed/, '');
  xml +=
    '<rss version="2.0" xmlns:itunes="http://www.itunes.com/dtds/podcast-1.0.dtd" xmlns:content="http://purl.org/rss/1.0/modules/content/" xmlns:atom="http://www.w3.org/2005/Atom" xmlns:media="http://search.yahoo.com/mrss/" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:snf="http://www.smartnews.be/snf">';
  xml += '<channel>';

  const results = await fetchFeedData(query);

  const feed = results.data.collection;
  xml += `<title>${
    feed && results.data.collection.title.replace(/&/, '&amp;')
  } - MPR News</title>`;
  xml += `<link>https://www.mprnews.org${url}</link>`;
  xml += `<atom:link
      href="https://www.mprnews.org/feed/${
        feed && results.data.collection.canonicalSlug
      }"
  rel="self"
  type="application/rss+xml"/> `;
  xml += `<description><![CDATA[${
    feed && results.data.collection.descriptionText
  }]]></description>`;
  xml += `<language>en-us</language>`;
  xml += `<lastBuildDate>${dayjs(
    new Date(feed && results.data.collection.publishDate)
  ).format(DATE_FORMAT)}</lastBuildDate>`;

  feed &&
    results.data.collection.results.items.forEach((item) => {
      let link = linkByTypeAs(item);
      if (item.resourceType != 'link') {
        link = `https://www.mprnews.org${link}`;
      }
      const tmpdte = greaterDate(item.publishDate, item.updatedAt);
      const dte = dayjs(new Date(tmpdte)).format(DATE_FORMAT);
      const title = item.shortTitle ? item.shortTitle : item.title;
      const authors = toSentence(item.contributors);
      const ele = React.createElement(Body, {
        nodeData: JSON.parse(item.body),
        embedded: parseEmbeddedAssets(item.embeddedAssets),
        minimal: false,
      });
      const markupImg = getImage(item);
      const markup = ReactDOMServer.renderToStaticMarkup(ele);
      let audio, enclosure;
      const hasAdio =
        item.audio.length > 0 &&
        item.audio[0].encodings.length > 0 &&
        item.audio[0].encodings[0].playFilePath;

      if (hasAdio) {
        audio = item.audio[0].encodings[0];
        enclosure = audio.playFilePath
          .split('?')[0]
          .replace(/%user_agent/, 'web');
      }
      xml += `<item>
                  <title>${title.replace(/&/, '&amp;')}</title>
                  <link>${link}</link>
                  <guid isPermaLink="true">${link}</guid>
                  <dc:creator>${authors}</dc:creator>
                  <pubDate>${dte}</pubDate>
                  <description><![CDATA[${item.descriptionText}]]></description>
                  <content:encoded><![CDATA[${markupImg}${markup}]]></content:encoded>`;
      if (markupImg) {
        xml += getImageXml(item);
      }
      if (hasAdio) {
        xml += `<enclosure url="${enclosure}" length="${audio.durationMs}" type="${audio.mediaType}" />`;
      }

      xml += `</item>`;
    });

  xml += '</channel>';
  xml += '</rss>';
  res.setHeader('Content-Type', 'text/xml');
  res.setHeader('Cache-Control', `public, max-age=60`);
  res.write(xml);
  res.end();
};

export default Rss;
